<script lang="ts">
import { useTailwind } from '@/plugins/useTailwind'
import { RealTimeEvents } from '@/services/real-time-events'
import { useInstantWinsStore } from '@/stores/instant-wins'
import { usePrizesStore } from '@/stores/prizes'
import type {
  Image,
  InstantWinResult,
  InstantWinResultsUpdateEvent,
} from '@/types'
import { PrizeResultCategories } from '@/utils/helper-objects'
import { defineComponent, useTemplateRef } from 'vue'

import AppCarousel from '../carousel/AppCarousel.vue'
import CarouselItem from '../carousel/CarouselItem.vue'
import InstantWinResultCard from '../members-zone/cards/InstantWinResultCard.vue'

type InstantWinResultWithImage = InstantWinResult & { image: Image }
export default defineComponent({
  components: {
    AppCarousel,
    CarouselItem,
    InstantWinResultCard,
  },
  setup() {
    const prizesStore = usePrizesStore()
    const instantWinsStore = useInstantWinsStore()
    const { breakpoints } = useTailwind()
    const carousel =
      useTemplateRef<InstanceType<typeof AppCarousel>>('carousel')

    return {
      breakpoints,
      carousel,
      instantWinsStore,
      PrizeResultCategories,
      prizesStore,
    }
  },
  data() {
    return {
      latestWinners: [] as Array<InstantWinResultWithImage>,
      prizeThreshold: 50000,
    }
  },
  computed: {
    instantWinsReady(): boolean {
      return (
        this.instantWinsStore.hasLoadedAll || this.instantWinsStore.hasError
      )
    },
  },
  watch: {
    // Wait for instant wins to finish loading (success or failure) before displaying results
    instantWinsReady: {
      handler(ready: boolean) {
        if (ready) {
          void this.loadInstantResults()
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.listenToRecentInstantResults()
  },
  methods: {
    async loadInstantResults() {
      const latestWinners = await this.instantWinsStore.fetchResults(
        1,
        this.prizeThreshold
      )

      this.latestWinners = latestWinners.rawData.data
        .map((result) => {
          const instantWin = this.instantWinsStore.instantWins.get(
            result.instant_win_code
          )

          if (!instantWin) {
            return null
          }

          return {
            ...result,
            image: instantWin.main_prize.card_image,
          }
        })
        .filter(
          (result): result is InstantWinResultWithImage => result !== null
        )
    },
    listenToRecentInstantResults() {
      RealTimeEvents.subscribe(
        RealTimeEvents.Channels.InstantWinResults,
        RealTimeEvents.Events.InstantWinResultsUpdate,
        true,
        (eventData: InstantWinResultsUpdateEvent) => {
          for (const instantWinResultData of eventData) {
            // Only show winners with prizes >= prizeThreshold on homepage
            if (instantWinResultData.prize_value < this.prizeThreshold) {
              continue
            }

            const instantWin = this.instantWinsStore.instantWins.get(
              instantWinResultData.instant_win_code
            )

            if (instantWin) {
              this.latestWinners.unshift({
                ...instantWinResultData,
                image: instantWin.main_prize.card_image,
              })
            }
          }

          // Scroll carousel to show the new winner(s)
          void this.$nextTick(() => {
            this.carousel?.scrollbox?.scrollTo(0)
          })
        }
      )
    },
  },
})
</script>
<template>
  <AppSection v-if="latestWinners.length" class="!pb-1">
    <AppCarousel
      ref="carousel"
      title="Our Latest Winners"
      :icon="Icons.Winners"
      :view-all-route="{
        name: 'results-and-winners',
        hash: '#' + PrizeResultCategories.InstantResults,
      }"
    >
      <CarouselItem
        v-for="latestWinner in latestWinners"
        :key="latestWinner.ticket"
      >
        <InstantWinResultCard
          :instant-win-result="latestWinner"
          class="animate__fadeInRight animate__animated animate__fast"
        />
      </CarouselItem>
    </AppCarousel>
  </AppSection>
</template>
